import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { DocumentMappingService } from 'src/service/document-mapping.service';
import { BasicService } from 'src/service/basic.service';
import { environment } from 'src/environments/environment';
import { Cr02DialogService } from 'src/app/shared/cr02-dialog/cr02-dialog.service';

interface HeaderSchemaField {
  key: string;
  label: string;
  required: boolean;
  custom?: boolean;
}

interface LineSchemaOption {
  label: string;
  required: boolean;
}

interface DetectedHeader {
  key: string;
  value: any;
  label: string;
}

interface DetectedColumn {
  key: string;
  sample: any;
}

interface CompletenessChip {
  label: string;
  done: boolean;
  target: 'hdr' | 'cols';
}

@Component({
  selector: 'app-document-viewer',
  templateUrl: './document-viewer.component.html',
  styleUrls: ['./document-viewer.component.scss']
})
export class DocumentViewerComponent implements OnInit {
  /** Standard header schema. Mandatory unless required === false. */
  readonly HEADER_SCHEMA: HeaderSchemaField[] = [
    { key: 'invno', label: 'Invoice Number', required: true },
    { key: 'invdate', label: 'Invoice Date', required: true },
    { key: 'pono', label: 'PO Number', required: true },
    { key: 'store', label: 'Store Code', required: true },
    { key: 'suppname', label: 'Supplier Name', required: false },
    { key: 'cur', label: 'Currency', required: true },
    { key: 'taxable', label: 'Taxable Value', required: true },
    { key: 'vat', label: 'VAT Amount', required: true },
    { key: 'vendorinv', label: 'Vendor Invoice Value', required: true },
    { key: 'netdisc', label: 'Net Value After Discount', required: false },
    { key: 'netaddchg', label: 'Net Value After Add Charges', required: false },
    { key: 'nettax', label: 'Net Value After Tax', required: true },
    { key: 'grand', label: 'Grand Total', required: true },
    { key: 'refno', label: 'Reference No.', required: false },
    { key: 'deliv', label: 'Delivery Date', required: false }
  ];

  readonly IGNORE_LABEL = '\u2014  Ignore this column  \u2014';

  /** Standard line-item column schema options for the dropdown. Index 0 = ignore. */
  readonly LINE_SCHEMA: LineSchemaOption[] = [
    { label: this.IGNORE_LABEL, required: false },
    { label: 'Item Code', required: true },
    { label: 'Description', required: true },
    { label: 'Pack Size', required: false },
    { label: 'Quantity', required: true },
    { label: 'UOM', required: false },
    { label: 'Unit Price', required: true },
    { label: 'Discount Amount 1', required: false },
    { label: 'Discount % 1', required: false },
    { label: 'Discount Amount 2', required: false },
    { label: 'Discount % 2', required: false },
    { label: 'Discount Amount 3', required: false },
    { label: 'Discount % 3', required: false },
    { label: 'VAT Rate', required: false },
    { label: 'Total Exclusive', required: true },
    { label: 'VAT Amount', required: false },
    { label: 'Total Inclusive', required: true }
  ];

  documentId = 0;
  extractedData: any = null;
  documentName = '';
  fileType = '';
  documentStatus: number | null = null;
  /** Supplier id from API (preferred over parsing extracted payload). */
  loadedSupplierId = '';
  /** Supplier name from master_suppliers (preferred). */
  loadedSupplierName = '';

  // document preview state (reused from original component)
  pdfUrl = '';
  pdfBlobUrl: SafeResourceUrl | null = null;
  sourceFileBlobUrl = '';
  spreadsheetRows: string[][] = [];
  spreadsheetColumnCount = 0;
  spreadsheetLoading = false;
  spreadsheetError = '';

  // detected fields from the extraction engine
  detectedHeaders: DetectedHeader[] = [];
  detectedColumns: DetectedColumn[] = [];

  // mapping state
  headerMapping: { [schemaKey: string]: string | null } = {};
  lineColumnMapping: { [detectedCol: string]: string | null } = {};
  /** Manual fallback values when no detected field is mapped / extracted. */
  headerManualValues: { [schemaKey: string]: string } = {};

  /** Extra optional header rows added by the admin. */
  extraHeaderFields: HeaderSchemaField[] = [];
  /** Standard optional header keys the admin chose to hide. */
  deletedHeaderKeys: string[] = [];
  /** Extra line-item schema labels available in the dropdown. */
  extraLineLabels: string[] = [];
  /** Manually added extracted line columns (name + sample value) shown on the LHS. */
  manualLineColumns: DetectedColumn[] = [];
  /** Detected line columns hidden from the mapping list. */
  hiddenLineColumns: string[] = [];
  /** Cached per-column dropdown options (avoids select reset when *ngFor rebuilds every CD). */
  lineLabelOptionsByCol: { [detectedCol: string]: LineSchemaOption[] } = {};
  private customManualLineSeq = 0;

  // legacy passthrough (kept for save payload compatibility)
  fieldVisibility: any = {};
  customFields: any = {};
  deletedFields: string[] = [];

  activeTab: 'hdr' | 'cols' = 'hdr';
  private customHeaderSeq = 0;
  /** True after mappings are restored so selects bind to saved values. */
  mappingsReady = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private documentMappingService: DocumentMappingService,
    public basicService: BasicService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private dialog: Cr02DialogService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.documentId = +params['id'];
      if (this.documentId) {
        this.loadDocument();
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------
  loadDocument(): void {
    this.basicService.showprogress = true;
    this.documentMappingService.getExtractedData(this.documentId).toPromise().then((response: any) => {
      this.basicService.showprogress = false;
      if (response && response.status === 'success') {
        this.extractedData = response.data;
        this.documentName = response.documentName || '';
        this.fileType = (response.fileType || '').toLowerCase();
        this.documentStatus = response.documentStatus ?? null;
        this.loadedSupplierId =
          response.supplierId !== undefined && response.supplierId !== null
            ? String(response.supplierId)
            : '';
        this.loadedSupplierName = (response.supplierName || '').trim();
        this.fieldVisibility = response.fieldVisibility || {};
        this.customFields = response.customFields || {};
        this.deletedFields = response.deletedFields || [];
        this.mappingsReady = false;
        this.restoreRowExtras(response);

        this.buildDetected();
        this.initMappings(response);

        this.loadDocumentFile();
        if (this.isSpreadsheetDocument()) {
          this.loadSpreadsheetPreview();
        }
      } else {
        this.dialog.error('Error', (response && response.message) || 'Failed to load document data').then(() => {
          this.goBack();
        });
      }
    }).catch(() => {
      this.basicService.showprogress = false;
      this.dialog.error('Error', 'Failed to load document data. Please try again.').then(() => {
        this.goBack();
      });
    });
  }

  /** Flatten detected header key/values and detected line columns from extractedData. */
  private buildDetected(): void {
    const seen = new Set<string>();
    const headers: DetectedHeader[] = [];

    const pushScalars = (obj: any, depth = 0) => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return;
      }
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (value !== null && typeof value === 'object') {
          // One level of nesting (e.g. invoice_data.Invoice_Details)
          if (!Array.isArray(value) && depth < 2) {
            pushScalars(value, depth + 1);
          }
          return;
        }
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        headers.push({ key, value, label: this.formatFieldName(key) });
      });
    };

    pushScalars(this.getInvoiceMetadata());
    pushScalars(this.getTotals());

    // Also pick up any top-level scalar metadata that lives directly on invoice_data.
    if (this.extractedData && this.extractedData.invoice_data && typeof this.extractedData.invoice_data === 'object') {
      pushScalars(this.extractedData.invoice_data);
    }
    pushScalars(this.extractedData);

    this.detectedHeaders = headers;

    // detected line columns
    const keys = this.getLineItemKeys();
    const firstRow = this.getLineItems()[0] || {};
    this.detectedColumns = keys.map(key => ({ key, sample: firstRow[key] }));
    this.mergeManualLineColumnsIntoDetected();
  }

  /** Append admin-entered extracted columns (name + value) onto the LHS list. */
  private mergeManualLineColumnsIntoDetected(): void {
    (this.manualLineColumns || []).forEach(col => {
      if (!col || !col.key) {
        return;
      }
      const existing = this.detectedColumns.find(c => c.key.toLowerCase() === col.key.toLowerCase());
      if (existing) {
        if (existing.sample === null || existing.sample === undefined || String(existing.sample).trim() === '') {
          existing.sample = col.sample;
        }
        return;
      }
      this.detectedColumns = [...this.detectedColumns, { key: col.key, sample: col.sample }];
    });
  }

  /** Set up headerMapping / lineColumnMapping from a saved payload or infer / auto-guess. */
  private initMappings(response: any): void {
    this.headerMapping = {};
    this.lineColumnMapping = {};
    this.visibleHeaderFields.forEach(f => (this.headerMapping[f.key] = null));
    this.detectedColumns.forEach(c => (this.lineColumnMapping[c.key] = null));

    const fm = this.normalizeFieldMappingPayload(response);
    const savedHeader = this.pickMappingObject(
      this.asMappingDict(response.headerMapping),
      this.asMappingDict(fm && fm.headerMapping),
      response.schemaVersion === 2 || (fm && fm.schemaVersion === 2)
    );
    const savedLine = this.pickMappingObject(
      this.asMappingDict(response.lineColumnMapping),
      this.asMappingDict(fm && fm.lineColumnMapping),
      response.schemaVersion === 2 || (fm && fm.schemaVersion === 2)
    );

    let restoredCount = 0;
    const usedDetected = new Set<string>();
    const usedLineLabels = new Set<string>();

    if (savedHeader) {
      Object.keys(savedHeader).forEach(key => {
        const schemaKey = this.resolveSchemaHeaderKey(key);
        const already =
          this.visibleHeaderFields.some(f => f.key === schemaKey) ||
          this.HEADER_SCHEMA.some(f => f.key === schemaKey) ||
          this.extraHeaderFields.some(f => f.key === schemaKey);
        if (!already && this.hasConcreteValue(savedHeader[key])) {
          this.extraHeaderFields.push({
            key: schemaKey,
            label: this.formatFieldName(key),
            required: false,
            custom: true
          });
        }
        if (this.headerMapping[schemaKey] === undefined) {
          this.headerMapping[schemaKey] = null;
        }
      });
      // Apply saved mappings using schema keys (and label aliases as keys).
      Object.keys(savedHeader).forEach(key => {
        const raw = savedHeader[key];
        if (!this.hasConcreteValue(raw)) {
          return;
        }
        const schemaKey = this.resolveSchemaHeaderKey(key);
        if (!schemaKey) {
          return;
        }
        const detectedKey = this.resolveDetectedHeaderKey(String(raw));
        if (!detectedKey) {
          return;
        }
        if (usedDetected.has(detectedKey) && this.headerMapping[schemaKey] !== detectedKey) {
          return;
        }
        usedDetected.add(detectedKey);
        this.headerMapping[schemaKey] = detectedKey;
        restoredCount++;
      });
    }

    if (savedLine) {
      Object.keys(savedLine).forEach(col => {
        const lbl = savedLine[col];
        if (typeof lbl === 'string' && lbl && lbl !== this.IGNORE_LABEL && !this.isValidLineLabel(lbl)) {
          if (!this.extraLineLabels.includes(lbl)) {
            this.extraLineLabels = [...this.extraLineLabels, lbl];
          }
        }
      });
      this.detectedColumns.forEach(c => {
        const rawLbl = savedLine[c.key];
        const raw = typeof rawLbl === 'string' ? rawLbl.trim() : '';
        const lbl = this.canonicalizeLineLabel(raw) || raw;
        const slot = this.discountSlotKey(lbl);
        const slotTaken = slot && this.isDiscountLabelUsed(usedLineLabels, slot);
        if (this.isValidLineLabel(lbl) && !usedLineLabels.has(lbl) && !slotTaken) {
          this.lineColumnMapping[c.key] = lbl;
          usedLineLabels.add(lbl);
          restoredCount++;
        } else {
          this.lineColumnMapping[c.key] = null;
        }
      });
      Object.keys(savedLine).forEach(col => {
        const rawLbl = savedLine[col];
        const raw = typeof rawLbl === 'string' ? rawLbl.trim() : '';
        const lbl = this.canonicalizeLineLabel(raw) || raw;
        const slot = this.discountSlotKey(lbl);
        if (!this.isValidLineLabel(lbl) || usedLineLabels.has(lbl) || (slot && this.isDiscountLabelUsed(usedLineLabels, slot))) {
          return;
        }
        if (!this.detectedColumns.some(c => c.key === col)) {
          this.detectedColumns = [...this.detectedColumns, { key: col, sample: '' }];
        }
        if (!this.lineColumnMapping[col]) {
          this.lineColumnMapping[col] = lbl;
          usedLineLabels.add(lbl);
          restoredCount++;
        }
        this.hiddenLineColumns = this.hiddenLineColumns.filter(h => h !== col);
      });
    }

    // Always try legacy aliases to fill gaps (and recover older templates).
    const legacyMap = this.getLegacyAliasMap(response, fm);
    if (legacyMap && Object.keys(legacyMap).length > 0) {
      if (this.inferFromLegacyMapping(legacyMap)) {
        restoredCount++;
      }
    }

    // Manual values also count as restored mapping state.
    if (Object.keys(this.headerManualValues || {}).length > 0) {
      restoredCount++;
    }

    if (restoredCount === 0) {
      this.autoGuess();
    }

    // Force new object refs so selects re-bind to restored values.
    this.headerMapping = { ...this.headerMapping };
    this.lineColumnMapping = { ...this.lineColumnMapping };
    this.detectedHeaders = [...this.detectedHeaders];
    this.detectedColumns = [...this.detectedColumns];
    this.refreshLineLabelOptions();
    this.mappingsReady = true;
  }

  private asMappingDict(value: any): Record<string, any> | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return value;
    }
    return null;
  }

  private hasConcreteValue(value: any): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  private hasMappingKeys(obj: any): boolean {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return false;
    }
    return Object.keys(obj).some(k => this.hasConcreteValue(obj[k]));
  }

  private pickMappingObject(primary: any, nested: any, preferSchemaV2: boolean): Record<string, any> | null {
    if (this.hasMappingKeys(primary)) {
      return primary;
    }
    if (preferSchemaV2 && this.hasMappingKeys(nested)) {
      return nested;
    }
    if (this.hasMappingKeys(nested)) {
      return nested;
    }
    return null;
  }

  /** Unwrap fieldMapping whether API returns aliases only or a nested schema-v2 blob. */
  private normalizeFieldMappingPayload(response: any): any {
    const fm = response?.fieldMapping;
    if (!fm || typeof fm !== 'object' || Array.isArray(fm)) {
      return {};
    }
    // Occasionally older clients nested the whole save payload under fieldMapping.
    if (fm.fieldMapping && typeof fm.fieldMapping === 'object') {
      return fm;
    }
    return fm;
  }

  private getLegacyAliasMap(response: any, fm: any): Record<string, any> {
    const candidate = fm && fm.fieldMapping && typeof fm.fieldMapping === 'object'
      ? fm.fieldMapping
      : fm;
    if (!candidate || typeof candidate !== 'object') {
      return {};
    }
    const out: Record<string, any> = {};
    Object.keys(candidate).forEach(key => {
      if ([
        'schemaVersion', 'headerMapping', 'lineColumnMapping', 'headerManualValues',
        'fieldMapping', 'fieldVisibility', 'customFields', 'deletedFields', 'mappingRows'
      ].includes(key)) {
        return;
      }
      const val = candidate[key];
      if (val !== null && val !== undefined && typeof val !== 'object') {
        out[key] = val;
      }
    });
    return out;
  }

  /** Match a saved schema field key (invno) or label ("Invoice Number") to HEADER_SCHEMA key. */
  private resolveSchemaHeaderKey(savedKey: string): string {
    if (!savedKey) {
      return savedKey;
    }
    if (this.HEADER_SCHEMA.some(f => f.key === savedKey) || this.extraHeaderFields.some(f => f.key === savedKey)) {
      return savedKey;
    }
    const lower = String(savedKey).toLowerCase().trim();
    const byKey = this.HEADER_SCHEMA.find(f => f.key.toLowerCase() === lower);
    if (byKey) {
      return byKey.key;
    }
    const norm = (s: string) => s.toLowerCase().replace(/[_\s.\-]+/g, '');
    const target = norm(savedKey);
    const byLabel = this.HEADER_SCHEMA.find(f => norm(f.label) === target);
    if (byLabel) {
      return byLabel.key;
    }
    const byExtra = this.extraHeaderFields.find(
      f => f.key.toLowerCase() === lower || norm(f.label) === target
    );
    return byExtra ? byExtra.key : savedKey;
  }

  /** Match a saved detected-field key to the current extracted headers (exact / case / label). */
  private resolveDetectedHeaderKey(savedKey: string): string | null {
    if (!savedKey) {
      return null;
    }
    const exact = this.detectedHeaders.find(d => d.key === savedKey);
    if (exact) {
      return exact.key;
    }
    const lower = savedKey.toLowerCase().trim();
    const byKey = this.detectedHeaders.find(d => d.key.toLowerCase() === lower);
    if (byKey) {
      return byKey.key;
    }
    const norm = (s: string) => s.toLowerCase().replace(/[_\s.\-]+/g, '');
    const target = norm(savedKey);
    const byLabel = this.detectedHeaders.find(
      d => norm(d.label) === target || norm(d.key) === target
    );
    if (byLabel) {
      return byLabel.key;
    }
    // Keep the saved key visible even if extraction no longer exposes it.
    this.ensureDetectedHeader(savedKey);
    return savedKey;
  }

  private ensureDetectedHeader(key: string, labelHint?: string): void {
    if (!key || this.detectedHeaders.some(d => d.key === key)) {
      return;
    }
    this.detectedHeaders = [
      ...this.detectedHeaders,
      { key, value: '', label: this.formatFieldName(labelHint || key) }
    ];
  }

  private isValidLineLabel(label: any): boolean {
    if (typeof label !== 'string' || !label || label === this.IGNORE_LABEL) {
      return false;
    }
    const canon = this.canonicalizeLineLabel(label);
    return this.lineSchemaOptions.some(o => o.label === label || o.label === canon);
  }

  private restoreRowExtras(response: any): void {
    const cf = response.customFields || this.customFields || {};
    const extras = cf.mappingRows || {};
    this.extraHeaderFields = Array.isArray(extras.headerExtras)
      ? extras.headerExtras.map((f: any) => ({
          key: String(f.key),
          label: String(f.label || f.key),
          required: false,
          custom: true
        }))
      : [];
    this.deletedHeaderKeys = Array.isArray(extras.deletedHeaderKeys)
      ? extras.deletedHeaderKeys.map((k: any) => String(k))
      : [];
    this.extraLineLabels = Array.isArray(extras.extraLineLabels)
      ? extras.extraLineLabels.map((l: any) => String(l)).filter(Boolean)
      : [];
    this.manualLineColumns = Array.isArray(extras.manualLineColumns)
      ? extras.manualLineColumns
          .map((c: any) => ({
            key: String(c.key || c.name || '').trim(),
            sample: c.sample != null ? c.sample : (c.value != null ? c.value : '')
          }))
          .filter((c: DetectedColumn) => !!c.key)
      : [];
    this.hiddenLineColumns = Array.isArray(extras.hiddenLineColumns)
      ? extras.hiddenLineColumns.map((c: any) => String(c))
      : [];
    this.customHeaderSeq = this.extraHeaderFields.length;
    this.customManualLineSeq = this.manualLineColumns.length;

    const manual =
      (response && response.headerManualValues) ||
      extras.headerManualValues ||
      {};
    this.headerManualValues = {};
    if (manual && typeof manual === 'object') {
      Object.keys(manual).forEach(key => {
        const val = manual[key];
        if (val !== null && val !== undefined && String(val).trim() !== '') {
          this.headerManualValues[key] = String(val).trim();
        }
      });
    }
  }

  private inferFromLegacyMapping(fm: any): boolean {
    let inferred = false;
    if (!fm || typeof fm !== 'object') {
      return false;
    }

    const usedDetected = new Set<string>();
    Object.keys(this.headerMapping || {}).forEach(k => {
      const mapped = this.headerMapping[k];
      if (mapped) {
        usedDetected.add(String(mapped));
      }
    });

    // header: legacy stored detectedKey -> standard label / extraction key.
    const headerAliasToKey: { [label: string]: string } = {};
    this.HEADER_SCHEMA.forEach(f => (headerAliasToKey[f.label.toLowerCase()] = f.key));
    const headerAliasExtras: { [label: string]: string } = {
      'invoice no': 'invno',
      'invoice no.': 'invno',
      'invoice number': 'invno',
      'invoice_number': 'invno',
      'invoice num': 'invno',
      'invoice date': 'invdate',
      'date': 'invdate',
      'po number': 'pono',
      'po no': 'pono',
      'po no.': 'pono',
      'order_no': 'pono',
      'order no': 'pono',
      'store code': 'store',
      'store_code': 'store',
      'supplier name': 'suppname',
      'supplier_name': 'suppname',
      'vendor name': 'suppname',
      'vendor_name': 'suppname',
      'currency': 'cur',
      'taxable value': 'taxable',
      'total_taxable_value': 'taxable',
      'total taxable value': 'taxable',
      'vendor invoice value': 'vendorinv',
      'vendor_invoice_value': 'vendorinv',
      'net value after discount': 'netdisc',
      'net_value_after_discount': 'netdisc',
      'net value after add charges': 'netaddchg',
      'net_value_after_add_charges': 'netaddchg',
      'net value after tax': 'nettax',
      'net_value_after_tax': 'nettax',
      'vat amount': 'vat',
      'vat': 'vat',
      'total_vat': 'vat',
      'grand total': 'grand',
      'grand_total': 'grand',
      'total inclusive': 'grand',
      'total inclusive of tax': 'grand',
      'reference no.': 'refno',
      'reference no': 'refno',
      'reference_number': 'refno',
      'delivery date': 'deliv',
      'delivery_date': 'deliv'
    };
    Object.keys(headerAliasExtras).forEach(k => (headerAliasToKey[k] = headerAliasExtras[k]));

    const lineAliasToLabel: { [alias: string]: string } = {
      'item code': 'Item Code',
      'code': 'Item Code',
      'description': 'Description',
      'pack size': 'Pack Size',
      'size': 'Pack Size',
      'quantity': 'Quantity',
      'qty': 'Quantity',
      'uom': 'UOM',
      'unit price': 'Unit Price',
      'price': 'Unit Price',
      'discount %': 'Discount % 1',
      'discount % 1': 'Discount % 1',
      'discount rate': 'Discount % 1',
      'discount rate 1': 'Discount % 1',
      'discount amount': 'Discount Amount 1',
      'discount amount 1': 'Discount Amount 1',
      'discount 1': 'Discount Amount 1',
      'discount%1': 'Discount % 1',
      'disc 1': 'Discount Amount 1',
      'disc1': 'Discount Amount 1',
      'discounts': 'Discount % 1',
      'item disc per 1': 'Discount % 1',
      'discount % 2': 'Discount % 2',
      'discount rate 2': 'Discount % 2',
      'discount amount 2': 'Discount Amount 2',
      'discount 2': 'Discount Amount 2',
      'discount%2': 'Discount % 2',
      'disc 2': 'Discount Amount 2',
      'disc2': 'Discount Amount 2',
      'item disc per 2': 'Discount % 2',
      'discount % 3': 'Discount % 3',
      'discount rate 3': 'Discount % 3',
      'discount amount 3': 'Discount Amount 3',
      'discount 3': 'Discount Amount 3',
      'discount%3': 'Discount % 3',
      'disc 3': 'Discount Amount 3',
      'disc3': 'Discount Amount 3',
      'item disc per 3': 'Discount % 3',
      'vat rate': 'VAT Rate',
      'vat_rate': 'VAT Rate',
      'vat %': 'VAT Rate',
      'total exclusive': 'Total Exclusive',
      'total inclusive': 'Total Inclusive',
      'vat amount': 'VAT Amount',
      'vat amt': 'VAT Amount'
    };

    // Primary shape: detectedKey -> schema label (does not require detectedHeaders first).
    Object.keys(fm).forEach(detectedKey => {
      const alias = fm[detectedKey];
      if (alias === null || alias === undefined || typeof alias === 'object') {
        return;
      }
      const schemaKey = headerAliasToKey[String(alias).toLowerCase().trim()];
      if (!schemaKey || !this.HEADER_SCHEMA.some(f => f.key === schemaKey) || this.headerMapping[schemaKey]) {
        return;
      }
      const dk = this.resolveDetectedHeaderKey(String(detectedKey));
      if (!dk || usedDetected.has(dk)) {
        return;
      }
      this.headerMapping[schemaKey] = dk;
      usedDetected.add(dk);
      inferred = true;
    });

    // Reverse shape: schema label -> detected key
    Object.keys(fm).forEach(key => {
      const schemaKey = headerAliasToKey[String(key).toLowerCase().trim()] || this.resolveSchemaHeaderKey(key);
      if (!this.HEADER_SCHEMA.some(f => f.key === schemaKey) && !this.extraHeaderFields.some(f => f.key === schemaKey)) {
        return;
      }
      const detectedKey = fm[key];
      if (!detectedKey || this.headerMapping[schemaKey]) {
        return;
      }
      const dk = this.resolveDetectedHeaderKey(String(detectedKey));
      if (!dk || usedDetected.has(dk)) {
        return;
      }
      this.headerMapping[schemaKey] = dk;
      usedDetected.add(dk);
      inferred = true;
    });

    // line: legacy stored detectedCol -> line schema label (or extraction key like Code/QTY).
    const usedLine = new Set<string>();
    Object.keys(this.lineColumnMapping || {}).forEach(col => {
      const lbl = this.lineColumnMapping[col];
      if (lbl) {
        usedLine.add(lbl);
      }
    });
    Object.keys(fm).forEach(col => {
      const alias = fm[col];
      if (this.lineColumnMapping[col] || alias === null || alias === undefined || typeof alias === 'object') {
        return;
      }
      const normalized =
        lineAliasToLabel[String(alias).toLowerCase().trim()] ||
        (this.isValidLineLabel(alias) ? String(alias) : null);
      if (!normalized || !this.isValidLineLabel(normalized) || usedLine.has(normalized)) {
        return;
      }
      // Only apply when this looks like a line column (exists or can be added).
      if (!this.detectedColumns.some(c => c.key === col)) {
        // Skip header-detected keys already used above unless column-like.
        if (this.detectedHeaders.some(d => d.key === col)) {
          return;
        }
        this.detectedColumns = [...this.detectedColumns, { key: col, sample: '' }];
      }
      this.lineColumnMapping[col] = normalized;
      usedLine.add(normalized);
      inferred = true;
    });

    return inferred;
  }

  // ---------------------------------------------------------------------------
  // Auto-guess helpers (used only when nothing has been saved yet)
  // ---------------------------------------------------------------------------
  private autoGuess(): void {
    const used = new Set<string>();
    const norm = (s: string) => (s || '').toLowerCase();

    const matchers: { key: string; test: (n: string) => boolean }[] = [
      { key: 'invno', test: n => n.includes('invoice') && (n.includes('num') || n.includes('no') || n.includes('#')) },
      { key: 'invdate', test: n => (n.includes('invoice') || n.includes('document')) && n.includes('date') },
      { key: 'pono', test: n => (n.includes('po') || n.includes('order')) && (n.includes('num') || n.includes('no')) },
      { key: 'store', test: n => n.includes('store') || n.includes('branch') },
      { key: 'suppname', test: n => (n.includes('supplier') || n.includes('vendor')) && n.includes('name') },
      { key: 'cur', test: n => n.includes('currency') },
      // Claim the dedicated CR-03 value fields before the generic taxable/grand so each
      // "Net Value After …" / "Vendor Invoice Value" column maps to its own schema row.
      {
        key: 'vendorinv',
        test: n => n.includes('vendor invoice value') || n.includes('vendor_invoice_value')
      },
      {
        key: 'netdisc',
        test: n => n.includes('after discount') || n.includes('net_value_after_discount')
      },
      {
        key: 'netaddchg',
        test: n =>
          n.includes('after add charge') ||
          n.includes('add charges') ||
          n.includes('net_value_after_add_charges')
      },
      {
        key: 'nettax',
        test: n =>
          n.includes('net value after tax') ||
          n.includes('net_value_after_tax') ||
          n.includes('after tax')
      },
      {
        key: 'grand',
        test: n =>
          n.includes('grand') ||
          (n.includes('inclusive') && (n.includes('total') || n.includes('value')))
      },
      {
        key: 'taxable',
        test: n => {
          if (
            n.includes('after tax') ||
            n.includes('inclusive') ||
            n.includes('grand')
          ) {
            return false;
          }
          return (
            n.includes('taxable') ||
            n.includes('subtotal') ||
            (n.includes('excl') && (n.includes('total') || n.includes('value')))
          );
        }
      },
      { key: 'vat', test: n => (n.includes('vat') || n.includes('tax')) && !n.includes('taxable') },
      { key: 'refno', test: n => n.includes('ref') },
      { key: 'deliv', test: n => n.includes('deliv') }
    ];

    matchers.forEach(m => {
      if (this.headerMapping[m.key]) {
        used.add(String(this.headerMapping[m.key]));
        return;
      }
      const hit = this.detectedHeaders.find(d => !used.has(d.key) && m.test(norm(d.label + ' ' + d.key)));
      if (hit) {
        this.headerMapping[m.key] = hit.key;
        used.add(hit.key);
      }
    });

    const usedLabels = new Set<string>();
    Object.keys(this.lineColumnMapping || {}).forEach(col => {
      const lbl = this.lineColumnMapping[col];
      if (lbl) {
        usedLabels.add(lbl);
      }
    });

    this.detectedColumns.forEach(c => {
      if (this.lineColumnMapping[c.key]) {
        return;
      }
      const n = norm(c.key);
      let label: string | null = null;
      if (n.includes('code') && !usedLabels.has('Item Code')) { label = 'Item Code'; }
      else if (n.includes('desc') && !usedLabels.has('Description')) { label = 'Description'; }
      else if ((n.includes('size') || n.includes('pack')) && !usedLabels.has('Pack Size')) { label = 'Pack Size'; }
      else if ((n.includes('qty') || n.includes('quantity')) && !usedLabels.has('Quantity')) { label = 'Quantity'; }
      else if ((n.includes('uom') || n.includes('measure')) && !usedLabels.has('UOM')) { label = 'UOM'; }
      else if (n.includes('price') && !usedLabels.has('Unit Price')) { label = 'Unit Price'; }
      else if (n.includes('disc')) {
        const discSlot = this.suggestDiscountLabel(n, usedLabels);
        if (discSlot) { label = discSlot; }
      }
      else if ((n.includes('vat') && (n.includes('rate') || n.includes('%') || n.includes('perc'))) && !usedLabels.has('VAT Rate')) { label = 'VAT Rate'; }
      else if ((n.includes('nett') || n.includes('net') || n.includes('excl')) && !usedLabels.has('Total Exclusive')) { label = 'Total Exclusive'; }
      else if (n.includes('vat') && !usedLabels.has('VAT Amount')) { label = 'VAT Amount'; }
      else if ((n.includes('total') || n.includes('incl')) && !usedLabels.has('Total Inclusive')) { label = 'Total Inclusive'; }
      this.lineColumnMapping[c.key] = label;
      if (label) {
        usedLabels.add(label);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Detected data extraction (kept compatible with both extraction shapes)
  // ---------------------------------------------------------------------------
  getInvoiceMetadata(): any {
    if (!this.extractedData) {
      return null;
    }
    if (this.extractedData.invoice_metadata && this.isPlainObject(this.extractedData.invoice_metadata)) {
      return this.extractedData.invoice_metadata;
    }
    const invoiceData = this.extractedData.invoice_data;
    if (invoiceData) {
      if (invoiceData.Invoice_Details && this.isPlainObject(invoiceData.Invoice_Details)) {
        return invoiceData.Invoice_Details;
      }
      for (const key in invoiceData) {
        if (key && (key.toLowerCase().includes('detail') || key.toLowerCase().includes('metadata'))) {
          if (this.isPlainObject(invoiceData[key]) && Object.keys(invoiceData[key]).length > 0) {
            return invoiceData[key];
          }
        }
      }
    }
    return null;
  }

  getTotals(): any {
    if (!this.extractedData) {
      return null;
    }
    if (this.extractedData.totals && this.isPlainObject(this.extractedData.totals)) {
      return this.extractedData.totals;
    }
    const invoiceData = this.extractedData.invoice_data;
    if (invoiceData) {
      if (invoiceData.Summary_Information && this.isPlainObject(invoiceData.Summary_Information)) {
        return invoiceData.Summary_Information;
      }
      for (const key in invoiceData) {
        if (key && (key.toLowerCase().includes('summary') || key.toLowerCase().includes('total'))) {
          if (this.isPlainObject(invoiceData[key]) && Object.keys(invoiceData[key]).length > 0) {
            return invoiceData[key];
          }
        }
      }
    }
    return null;
  }

  getLineItems(): any[] {
    if (this.extractedData && Array.isArray(this.extractedData.line_items) && this.extractedData.line_items.length) {
      return this.extractedData.line_items;
    }
    if (this.extractedData && this.extractedData.invoice_data &&
        Array.isArray(this.extractedData.invoice_data.item_details) &&
        this.extractedData.invoice_data.item_details.length) {
      return this.extractedData.invoice_data.item_details;
    }
    return [];
  }

  getLineItemKeys(): string[] {
    const items = this.getLineItems();
    return items.length ? Object.keys(items[0]) : [];
  }

  private isPlainObject(value: any): boolean {
    return value && typeof value === 'object' && !Array.isArray(value);
  }

  // ---------------------------------------------------------------------------
  // Header mapping UX
  // ---------------------------------------------------------------------------
  /** Detected headers still available for a schema field (unused elsewhere + current selection). */
  getAvailableDetected(schemaKey: string): DetectedHeader[] {
    const current = this.headerMapping[schemaKey] ? String(this.headerMapping[schemaKey]) : null;
    const usedElsewhere = new Set<string>();
    Object.keys(this.headerMapping || {}).forEach(k => {
      const mapped = this.headerMapping[k];
      if (k !== schemaKey && mapped) {
        usedElsewhere.add(String(mapped));
      }
    });
    const opts = this.detectedHeaders.filter(d => !usedElsewhere.has(d.key));
    // Always keep the current selection visible even if another row also claims it.
    if (current && !opts.some(d => d.key === current)) {
      const found = this.detectedHeaders.find(d => d.key === current);
      opts.unshift(found || { key: current, value: '', label: this.formatFieldName(current) });
    }
    return opts;
  }

  onHeaderChange(schemaKey: string, detectedKey: string | null): void {
    const nextKey = detectedKey && String(detectedKey).trim() ? String(detectedKey) : null;
    this.headerMapping = {
      ...this.headerMapping,
      [schemaKey]: nextKey
    };
    // If a real extracted value is now available, drop the manual fallback.
    if (nextKey && this.getExtractedHeaderValue(schemaKey)) {
      const next = { ...this.headerManualValues };
      delete next[schemaKey];
      this.headerManualValues = next;
    }
  }

  onManualHeaderChange(schemaKey: string, value: string): void {
    const trimmed = (value || '').trim();
    this.headerManualValues = {
      ...this.headerManualValues,
      [schemaKey]: trimmed
    };
    if (trimmed && !this.headerMapping[schemaKey]) {
      // Keep dropdown on "not mapped" while using manual entry.
      this.headerMapping = {
        ...this.headerMapping,
        [schemaKey]: null
      };
    }
  }

  /** Raw extracted value for a mapped detected field (no manual fallback). */
  getExtractedHeaderValue(schemaKey: string): string {
    const dk = this.headerMapping[schemaKey];
    if (!dk) {
      return '';
    }
    const found = this.detectedHeaders.find(d => d.key === dk);
    if (!found) {
      return '';
    }
    const amountKeys = new Set(['taxable', 'vat', 'grand', 'vendorinv', 'netdisc', 'netaddchg', 'nettax']);
    return this.formatDisplayValue(found.value, amountKeys.has(schemaKey));
  }

  getHeaderPreview(schemaKey: string): string {
    return this.getExtractedHeaderValue(schemaKey);
  }

  getHeaderManualValue(schemaKey: string): string {
    return this.headerManualValues[schemaKey] || '';
  }

  needsManualHeaderEntry(schemaKey: string): boolean {
    return !this.getExtractedHeaderValue(schemaKey);
  }

  hasHeaderValue(schemaKey: string): boolean {
    return !!this.getExtractedHeaderValue(schemaKey) || !!this.getHeaderManualValue(schemaKey).trim();
  }

  formatDetectedOption(detected: DetectedHeader, schemaKey: string): string {
    const amountKeys = new Set(['taxable', 'vat', 'grand', 'cur', 'vendorinv', 'netdisc', 'netaddchg', 'nettax']);
    return this.formatDisplayValue(detected.value, amountKeys.has(schemaKey) && schemaKey !== 'cur');
  }

  isHeaderTodo(field: HeaderSchemaField): boolean {
    return field.required && !this.hasHeaderValue(field.key);
  }

  get visibleHeaderFields(): HeaderSchemaField[] {
    const deleted = new Set(this.deletedHeaderKeys);
    const base = this.HEADER_SCHEMA.filter(f => f.required || !deleted.has(f.key));
    return [...base, ...this.extraHeaderFields];
  }

  get visibleLineColumns(): DetectedColumn[] {
    const hidden = new Set(this.hiddenLineColumns);
    return this.detectedColumns.filter(c => !hidden.has(c.key));
  }

  get lineSchemaOptions(): LineSchemaOption[] {
    const base = this.LINE_SCHEMA.filter(o => o.label !== this.IGNORE_LABEL);
    const extras = this.extraLineLabels
      .filter(l => !base.some(b => b.label.toLowerCase() === l.toLowerCase()))
      .map(label => ({ label, required: false }));
    return [{ label: this.IGNORE_LABEL, required: false }, ...base, ...extras];
  }

  /** Rebuild cached dropdown options for every visible line column. */
  refreshLineLabelOptions(): void {
    const next: { [detectedCol: string]: LineSchemaOption[] } = {};
    this.visibleLineColumns.forEach(col => {
      next[col.key] = this.computeAvailableLineLabels(col.key);
    });
    this.lineLabelOptionsByCol = next;
  }

  /**
   * Independent discount slots:
   * - amt1/2/3 = Discount Amount N
   * - pct1/2/3 = Discount % N (Discount Rate is a legacy alias of %)
   */
  private discountSlotKey(label: string | null | undefined): string {
    const lower = String(label || '').trim().toLowerCase();
    if (lower === 'discount amount' || lower === 'discount amount 1') {
      return 'amt1';
    }
    if (lower === 'discount amount 2') {
      return 'amt2';
    }
    if (lower === 'discount amount 3') {
      return 'amt3';
    }
    if (
      lower === 'discount %' ||
      lower === 'discount % 1' ||
      lower === 'discount%' ||
      lower === 'discount%1' ||
      lower === 'discount rate' ||
      lower === 'discount rate 1' ||
      lower === 'discounts'
    ) {
      return 'pct1';
    }
    if (
      lower === 'discount % 2' ||
      lower === 'discount%2' ||
      lower === 'discount rate 2'
    ) {
      return 'pct2';
    }
    if (
      lower === 'discount % 3' ||
      lower === 'discount%3' ||
      lower === 'discount rate 3'
    ) {
      return 'pct3';
    }
    return '';
  }

  private discountAliasesForSlot(slot: string): string[] {
    if (slot === 'amt1') {
      return ['Discount Amount 1', 'Discount Amount'];
    }
    if (slot === 'amt2') {
      return ['Discount Amount 2'];
    }
    if (slot === 'amt3') {
      return ['Discount Amount 3'];
    }
    // Discount Rate is a legacy alias of Discount % only.
    if (slot === 'pct1') {
      return ['Discount % 1', 'Discount %', 'Discount Rate 1', 'Discount Rate'];
    }
    if (slot === 'pct2') {
      return ['Discount % 2', 'Discount Rate 2'];
    }
    if (slot === 'pct3') {
      return ['Discount % 3', 'Discount Rate 3'];
    }
    return [];
  }

  private discountSlotChipLabel(slot: string): string {
    if (slot === 'amt1') {
      return 'Discount Amount 1';
    }
    if (slot === 'amt2') {
      return 'Discount Amount 2';
    }
    if (slot === 'amt3') {
      return 'Discount Amount 3';
    }
    if (slot === 'pct1') {
      return 'Discount % 1';
    }
    if (slot === 'pct2') {
      return 'Discount % 2';
    }
    if (slot === 'pct3') {
      return 'Discount % 3';
    }
    return slot;
  }

  private isDiscountLabelUsed(usedLabels: Set<string>, slot: string): boolean {
    return this.discountAliasesForSlot(slot).some(
      a => usedLabels.has(a) || usedLabels.has(a.toLowerCase())
    );
  }

  /**
   * Normalize legacy labels for slot conflict checks.
   * Discount Rate ≡ Discount % only. Discount Amount stays separate.
   */
  private canonicalizeLineLabel(label: string | null | undefined): string {
    const lbl = String(label || '').trim();
    if (!lbl) {
      return '';
    }
    const lower = lbl.toLowerCase();
    // Discount Rate → Discount % only
    if (
      lower === 'discount rate' ||
      lower === 'discount rate 1' ||
      lower === 'discount %' ||
      lower === 'discount%' ||
      lower === 'discount % 1' ||
      lower === 'discount%1' ||
      lower === 'discounts'
    ) {
      return 'Discount % 1';
    }
    if (
      lower === 'discount rate 2' ||
      lower === 'discount % 2' ||
      lower === 'discount%2'
    ) {
      return 'Discount % 2';
    }
    if (
      lower === 'discount rate 3' ||
      lower === 'discount % 3' ||
      lower === 'discount%3'
    ) {
      return 'Discount % 3';
    }
    if (lower === 'discount amount' || lower === 'discount amount 1') {
      return 'Discount Amount 1';
    }
    if (lower === 'discount amount 2') {
      return 'Discount Amount 2';
    }
    if (lower === 'discount amount 3') {
      return 'Discount Amount 3';
    }
    return lbl;
  }

  /** Auto-map detected discount columns; Amount and % are independent. */
  private suggestDiscountLabel(normalizedCol: string, usedLabels: Set<string>): string | null {
    const amountSlots = ['Discount Amount 1', 'Discount Amount 2', 'Discount Amount 3'];
    const percentSlots = ['Discount % 1', 'Discount % 2', 'Discount % 3'];
    const n = normalizedCol || '';
    const looksLikePercent =
      n.includes('discount %') ||
      n.includes('discount%') ||
      n.includes('discount rate') ||
      n.includes('discount_rate') ||
      n.includes('%') ||
      n.includes('per 1') ||
      n.includes('per 2') ||
      n.includes('per 3') ||
      n.includes('per1') ||
      n.includes('per2') ||
      n.includes('per3') ||
      n.includes('item disc per');
    const looksLikeAmount =
      n.includes('discount amount') ||
      n.includes('discount_amount') ||
      n.includes('disc amt') ||
      n.includes('discount amt');
    const slots =
      looksLikeAmount && !looksLikePercent
        ? amountSlots
        : looksLikePercent
          ? percentSlots
          : amountSlots;
    const slotPrefix = slots === percentSlots ? 'pct' : 'amt';
    // Prefer numbered column names (Disc1 / Discount 2 / ITEM DISC PER 3).
    for (let i = 1; i <= 3; i++) {
      const slotLabel = slots[i - 1];
      const slotKey = `${slotPrefix}${i}`;
      const numbered =
        n.includes(`disc ${i}`) ||
        n.includes(`disc${i}`) ||
        n.includes(`discount ${i}`) ||
        n.includes(`discount%${i}`) ||
        n.includes(`discount_${i}`) ||
        n.includes(`discount rate ${i}`) ||
        n.includes(`discount_rate_${i}`) ||
        n.includes(`discount amount ${i}`) ||
        n.includes(`discount_amount_${i}`) ||
        n.includes(`discount % ${i}`) ||
        n.includes(`per ${i}`) ||
        n.includes(`per${i}`) ||
        new RegExp(`(?:^|[^0-9])${i}(?:[^0-9]|$)`).test(n);
      if (numbered && !this.isDiscountLabelUsed(usedLabels, slotKey)) {
        return slotLabel;
      }
    }
    for (let i = 1; i <= 3; i++) {
      const slotLabel = slots[i - 1];
      if (!this.isDiscountLabelUsed(usedLabels, `${slotPrefix}${i}`)) {
        return slotLabel;
      }
    }
    return null;
  }

  /** Line schema labels still available for a detected column (each label usable once). */
  getAvailableLineLabels(detectedCol: string): LineSchemaOption[] {
    return this.lineLabelOptionsByCol[detectedCol] || this.computeAvailableLineLabels(detectedCol);
  }

  private computeAvailableLineLabels(detectedCol: string): LineSchemaOption[] {
    const currentRaw = this.lineColumnMapping[detectedCol] || null;
    const current = currentRaw;
    const usedElsewhere = new Set<string>();
    Object.keys(this.lineColumnMapping || {}).forEach(col => {
      const raw = this.lineColumnMapping[col];
      const lbl = this.canonicalizeLineLabel(raw) || raw;
      if (col !== detectedCol && lbl) {
        usedElsewhere.add(lbl);
        usedElsewhere.add(lbl.toLowerCase());
        if (raw) {
          usedElsewhere.add(raw);
          usedElsewhere.add(String(raw).toLowerCase());
        }
        // Only mark aliases of the same independent slot (Amount ≠ %).
        const slot = this.discountSlotKey(raw || lbl);
        this.discountAliasesForSlot(slot).forEach(alias => {
          usedElsewhere.add(alias);
          usedElsewhere.add(alias.toLowerCase());
        });
      }
    });
    const opts = this.lineSchemaOptions.filter(o => {
      if (o.label === this.IGNORE_LABEL) {
        return true;
      }
      return !usedElsewhere.has(o.label) && !usedElsewhere.has(o.label.toLowerCase());
    });
    if (current && current !== this.IGNORE_LABEL && !opts.some(o => o.label === current || o.label === currentRaw)) {
      opts.push({ label: current, required: false });
    }
    return opts;
  }

  canDeleteHeaderRow(field: HeaderSchemaField): boolean {
    return !!field.custom || !field.required;
  }

  addHeaderMappingRow(): void {
    this.dialog.prompt({
      title: 'Add Mapping Row',
      message: 'Enter a label for the new header field mapping.',
      inputLabel: 'Field label',
      inputPlaceholder: 'e.g. Delivery Note No.',
      confirmText: 'Add',
      cancelText: 'Cancel'
    }).then((value) => {
      if (value === null) {
        return;
      }
      const label = value.trim();
      if (!label) {
        this.dialog.warning('Label Required', 'Please enter a field label.');
        return;
      }
      const exists = this.visibleHeaderFields.some(f => f.label.toLowerCase() === label.toLowerCase());
      if (exists) {
        this.dialog.warning('Already Exists', `A mapping row named "${label}" already exists.`);
        return;
      }
      this.customHeaderSeq += 1;
      const key = `custom_${this.customHeaderSeq}_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`.replace(/_+$/, '');
      this.extraHeaderFields = [...this.extraHeaderFields, { key, label, required: false, custom: true }];
      this.headerMapping[key] = null;
      this.activeTab = 'hdr';
    });
  }

  deleteHeaderMappingRow(field: HeaderSchemaField): void {
    if (!this.canDeleteHeaderRow(field)) {
      this.dialog.warning('Cannot Delete', 'Mandatory header fields cannot be removed.');
      return;
    }
    this.dialog.confirm({
      title: 'Delete Mapping Row?',
      message: `Remove "${field.label}" from this vendor template?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      danger: true
    }).then((ok) => {
      if (!ok) {
        return;
      }
      const next = { ...this.headerMapping };
      delete next[field.key];
      this.headerMapping = next;
      const manualNext = { ...this.headerManualValues };
      delete manualNext[field.key];
      this.headerManualValues = manualNext;
      if (field.custom) {
        this.extraHeaderFields = this.extraHeaderFields.filter(f => f.key !== field.key);
      } else if (!this.deletedHeaderKeys.includes(field.key)) {
        this.deletedHeaderKeys = [...this.deletedHeaderKeys, field.key];
      }
    });
  }

  addLineMappingRow(): void {
    this.dialog.promptDual({
      title: 'Add Column',
      message:
        'Enter the extracted column name and its sample value. This adds a left-side extracted field you can map to a schema label.',
      inputLabel: 'Column name',
      inputPlaceholder: 'e.g. VAT Rate',
      inputLabel2: 'Extracted value',
      inputPlaceholder2: 'e.g. 14%',
      confirmText: 'Add column',
      cancelText: 'Cancel'
    }).then((result) => {
      if (!result) {
        return;
      }
      const name = (result.value || '').trim();
      const sample = (result.value2 || '').trim();
      if (!name) {
        this.dialog.warning('Name Required', 'Please enter a column name.');
        return;
      }
      if (!sample) {
        this.dialog.warning('Value Required', 'Please enter the extracted value for this column.');
        return;
      }
      if (this.detectedColumns.some(c => c.key.toLowerCase() === name.toLowerCase())) {
        this.dialog.warning('Already Exists', `An extracted column named "${name}" already exists on the left.`);
        return;
      }

      this.customManualLineSeq += 1;
      const col: DetectedColumn = { key: name, sample };
      this.manualLineColumns = [...this.manualLineColumns, col];
      this.detectedColumns = [...this.detectedColumns, col];
      this.hiddenLineColumns = this.hiddenLineColumns.filter(h => h.toLowerCase() !== name.toLowerCase());
      this.lineColumnMapping = {
        ...this.lineColumnMapping,
        [name]: null
      };
      this.refreshLineLabelOptions();
      this.activeTab = 'cols';
    });
  }

  deleteLineMappingRow(col: DetectedColumn): void {
    this.dialog.confirm({
      title: 'Delete Mapping Row?',
      message: `Hide detected column "${col.key}" from this vendor template?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      danger: true
    }).then((ok) => {
      if (!ok) {
        return;
      }
      this.lineColumnMapping = {
        ...this.lineColumnMapping,
        [col.key]: null
      };
      // Manual extracted columns are removed entirely; engine-detected ones are only hidden.
      if (this.manualLineColumns.some(c => c.key === col.key)) {
        this.manualLineColumns = this.manualLineColumns.filter(c => c.key !== col.key);
        this.detectedColumns = this.detectedColumns.filter(c => c.key !== col.key);
        const next = { ...this.lineColumnMapping };
        delete next[col.key];
        this.lineColumnMapping = next;
      } else if (!this.hiddenLineColumns.includes(col.key)) {
        this.hiddenLineColumns = [...this.hiddenLineColumns, col.key];
      }
      this.refreshLineLabelOptions();
    });
  }

  // ---------------------------------------------------------------------------
  // Line-item column mapping UX
  // ---------------------------------------------------------------------------
  onLineChange(detectedCol: string, label: string | null): void {
    const raw = label == null ? '' : String(label);
    const next = raw.trim() && raw !== this.IGNORE_LABEL ? raw.trim() : null;
    this.lineColumnMapping = {
      ...this.lineColumnMapping,
      [detectedCol]: next
    };
    // Defer so the native <select> finishes applying the chosen value before options refresh.
    setTimeout(() => this.refreshLineLabelOptions(), 0);
  }

  isColumnIgnored(detectedCol: string): boolean {
    return !this.lineColumnMapping[detectedCol];
  }

  trackLineLabelOption(_index: number, opt: LineSchemaOption): string {
    return opt.label;
  }

  /** Detected columns that map to a real schema label, in detected order (for the live preview). */
  getMappedColumns(): DetectedColumn[] {
    return this.visibleLineColumns.filter(c => !!this.lineColumnMapping[c.key]);
  }

  getPreviewRows(): any[] {
    return this.getLineItems().slice(0, 5);
  }

  getColumnValue(row: any, detectedCol: string): string {
    if (row && row[detectedCol] !== null && row[detectedCol] !== undefined && String(row[detectedCol]).trim() !== '') {
      return this.formatDisplayValue(row[detectedCol], true);
    }
    const manual = this.detectedColumns.find(c => c.key === detectedCol);
    return this.formatDisplayValue(manual ? manual.sample : '', true);
  }

  private stringifyValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  /**
   * CR-05: strip erroneous Rand "R" prefixes from Botswana Pula amounts
   * and normalise currency display for the template editor previews.
   */
  private formatDisplayValue(value: any, isAmountHint = false): string {
    const raw = this.stringifyValue(value).trim();
    if (!raw) {
      return '';
    }
    const currencyCode = this.currencyCodeHint();
    // Strip leading Rand symbol when document currency is not ZAR.
    const stripped = raw.replace(/^R\s*(?=\d)/i, '').trim();
    if (isAmountHint || /^R\s*\d/i.test(raw)) {
      if (currencyCode && currencyCode !== 'ZAR' && currencyCode !== 'R') {
        return stripped !== raw ? `${currencyCode} ${stripped}` : raw;
      }
      if (/^R\s*\d/i.test(raw) && (!currencyCode || currencyCode === 'BWP' || /pula/i.test(this.detectedCurrency))) {
        return `BWP ${stripped}`;
      }
    }
    return stripped !== raw && currencyCode && currencyCode !== 'ZAR' ? stripped : raw;
  }

  private currencyCodeHint(): string {
    const cur = (this.detectedCurrency || '').toString().trim();
    if (!cur || cur === '\u2014') {
      return '';
    }
    const upper = cur.toUpperCase();
    if (/\bBWP\b/.test(upper) || /PULA/.test(upper)) {
      return 'BWP';
    }
    if (/\bZAR\b/.test(upper) || /\bRAND\b/.test(upper)) {
      return 'ZAR';
    }
    const m = upper.match(/\b([A-Z]{3})\b/);
    return m ? m[1] : '';
  }

  // ---------------------------------------------------------------------------
  // Completeness
  // ---------------------------------------------------------------------------
  get mandatoryHeaderFields(): HeaderSchemaField[] {
    return this.HEADER_SCHEMA.filter(f => f.required);
  }

  /**
   * Required line fields for publish completeness.
   * Discount Amount N and Discount % N are independent (both can be mapped).
   */
  private get mandatoryLineCompletenessItems(): { label: string; aliases: string[] }[] {
    const items: { label: string; aliases: string[] }[] = [];
    const seenDiscSlots = new Set<string>();
    this.LINE_SCHEMA.forEach(o => {
      if (!o.required || o.label === this.IGNORE_LABEL) {
        return;
      }
      const slot = this.discountSlotKey(o.label);
      if (slot) {
        if (seenDiscSlots.has(slot)) {
          return;
        }
        seenDiscSlots.add(slot);
        items.push({
          label: this.discountSlotChipLabel(slot),
          aliases: this.discountAliasesForSlot(slot)
        });
        return;
      }
      items.push({ label: o.label, aliases: [o.label] });
    });
    return items;
  }

  get mandatoryLineLabels(): string[] {
    return this.mandatoryLineCompletenessItems.map(i => i.label);
  }

  private get mappedLineLabels(): Set<string> {
    const set = new Set<string>();
    Object.keys(this.lineColumnMapping).forEach(col => {
      const lbl = this.lineColumnMapping[col];
      if (lbl) {
        set.add(lbl);
      }
    });
    return set;
  }

  private isLineCompletenessItemDone(
    item: { label: string; aliases: string[] },
    mapped: Set<string>
  ): boolean {
    return item.aliases.some(a => mapped.has(a));
  }

  get headerDoneCount(): number {
    return this.mandatoryHeaderFields.filter(f => this.hasHeaderValue(f.key)).length;
  }

  get lineDoneCount(): number {
    const mapped = this.mappedLineLabels;
    return this.mandatoryLineCompletenessItems.filter(i =>
      this.isLineCompletenessItemDone(i, mapped)
    ).length;
  }

  get totalMandatory(): number {
    return this.mandatoryHeaderFields.length + this.mandatoryLineLabels.length;
  }

  get doneMandatory(): number {
    return this.headerDoneCount + this.lineDoneCount;
  }

  get completenessPct(): number {
    if (this.totalMandatory === 0) {
      return 0;
    }
    return Math.round((this.doneMandatory / this.totalMandatory) * 100);
  }

  get headerRemaining(): number {
    return this.mandatoryHeaderFields.length - this.headerDoneCount;
  }

  get lineRemaining(): number {
    return this.mandatoryLineLabels.length - this.lineDoneCount;
  }

  get completenessChips(): CompletenessChip[] {
    const mapped = this.mappedLineLabels;
    const chips: CompletenessChip[] = [];
    this.mandatoryHeaderFields.forEach(f => {
      chips.push({ label: f.label, done: this.hasHeaderValue(f.key), target: 'hdr' });
    });
    this.mandatoryLineCompletenessItems.forEach(item => {
      chips.push({
        label: item.label + ' (line)',
        done: this.isLineCompletenessItemDone(item, mapped),
        target: 'cols'
      });
    });
    return chips;
  }

  get canPublish(): boolean {
    return this.doneMandatory >= this.totalMandatory && this.totalMandatory > 0;
  }

  // ---------------------------------------------------------------------------
  // Status / header meta
  // ---------------------------------------------------------------------------
  get isPublished(): boolean {
    return this.documentStatus === 5;
  }

  get statusLabel(): string {
    return this.isPublished ? 'Published' : 'Draft';
  }

  get supplierName(): string {
    if (this.loadedSupplierName) {
      return this.loadedSupplierName;
    }
    const ed = this.extractedData || {};
    const invoiceData = ed.invoice_data || {};
    const details = this.getInvoiceMetadata() || {};
    const supplierBlock = ed.supplier_data || ed.supplier || invoiceData.Supplier_Information || {};
    return (
      supplierBlock['Supplier Name'] ||
      supplierBlock['supplier_name'] ||
      supplierBlock['supplierName'] ||
      details['supplier_name'] ||
      details['vendor_name'] ||
      details['supplierName'] ||
      ed.supplierName ||
      this.documentName ||
      'Vendor'
    );
  }

  get supplierId(): string {
    if (this.loadedSupplierId && this.loadedSupplierId.trim() !== '') {
      return this.loadedSupplierId;
    }
    const ed = this.extractedData || {};
    const invoiceData = ed.invoice_data || {};
    const details = this.getInvoiceMetadata() || {};
    const supplierBlock = ed.supplier_data || ed.supplier || invoiceData.Supplier_Information || {};
    const id =
      supplierBlock['supplier_id'] ||
      supplierBlock['supplierId'] ||
      details['supplier_id'] ||
      details['supplierId'] ||
      ed.supplierId;
    return id !== undefined && id !== null && String(id).trim() !== '' ? String(id) : '\u2014';
  }

  get detectedCurrency(): string {
    // Prefer the value chosen for the Currency schema field, else any detected currency field.
    const mapped = this.headerMapping['cur'];
    if (mapped) {
      const found = this.detectedHeaders.find(d => d.key === mapped);
      if (found && this.stringifyValue(found.value)) {
        return this.stringifyValue(found.value);
      }
    }
    const currencyField = this.detectedHeaders.find(d => (d.label + ' ' + d.key).toLowerCase().includes('currency'));
    if (currencyField && this.stringifyValue(currencyField.value)) {
      return this.stringifyValue(currencyField.value);
    }
    return '\u2014';
  }

  // ---------------------------------------------------------------------------
  // Document preview (reused from the original component)
  // ---------------------------------------------------------------------------
  isPdfDocument(): boolean {
    return this.fileType === 'pdf';
  }

  isSpreadsheetDocument(): boolean {
    return ['xls', 'xlsx', 'xlsm'].includes(this.fileType);
  }

  loadDocumentFile(): void {
    const userData = this.basicService.user_data || this.basicService.getItem('user_data');
    const token = userData?.loginToken || userData?.apiKey || '';
    if (!token) {
      return;
    }
    const headers: any = { 'Authorization': 'Bearer ' + token };
    this.http.get(`${environment.auth_url}/api/getDocumentFile/${this.documentId}`, {
      headers,
      responseType: 'blob'
    }).toPromise().then((blob: any) => {
      const url = window.URL.createObjectURL(blob);
      this.sourceFileBlobUrl = url;
      if (this.isPdfDocument()) {
        this.pdfBlobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url + '#navpanes=0');
      }
    }).catch((error: any) => {
      console.error('Error loading document file:', error);
    });
  }

  loadSpreadsheetPreview(): void {
    this.spreadsheetLoading = true;
    this.spreadsheetError = '';
    this.documentMappingService.getSheetPreview(this.documentId).toPromise().then((response: any) => {
      this.spreadsheetLoading = false;
      if (response && response.status === 'success') {
        this.spreadsheetRows = response.rows || [];
        this.spreadsheetColumnCount = this.spreadsheetRows.reduce((max, row) => Math.max(max, row.length), 0);
      } else {
        this.spreadsheetError = (response && response.message) || 'Failed to load spreadsheet preview';
        this.spreadsheetRows = [];
        this.spreadsheetColumnCount = 0;
      }
    }).catch(() => {
      this.spreadsheetLoading = false;
      this.spreadsheetError = 'Failed to load spreadsheet preview';
      this.spreadsheetRows = [];
      this.spreadsheetColumnCount = 0;
    });
  }

  getSpreadsheetColumnLetters(): string[] {
    const letters: string[] = [];
    for (let i = 0; i < this.spreadsheetColumnCount; i++) {
      letters.push(this.getExcelColumnLetter(i));
    }
    return letters;
  }

  getExcelColumnLetter(index: number): string {
    let n = index;
    let label = '';
    while (n >= 0) {
      label = String.fromCharCode((n % 26) + 65) + label;
      n = Math.floor(n / 26) - 1;
    }
    return label;
  }

  getSpreadsheetCellValue(row: string[], colIndex: number): string {
    const value = row[colIndex];
    return value !== null && value !== undefined && value !== '' ? String(value) : '';
  }

  getPdfUrlWithAuth(): SafeResourceUrl | string {
    return this.pdfBlobUrl || this.pdfUrl;
  }

  downloadSourceFile(): void {
    if (!this.sourceFileBlobUrl) {
      return;
    }
    const link = document.createElement('a');
    link.href = this.sourceFileBlobUrl;
    link.download = this.documentName || `document-${this.documentId}.${this.fileType || 'file'}`;
    link.click();
  }

  // ---------------------------------------------------------------------------
  // Tabs / navigation
  // ---------------------------------------------------------------------------
  setTab(tab: 'hdr' | 'cols'): void {
    this.activeTab = tab;
  }

  goBack(): void {
    this.router.navigate(['/admin/documentmapping']);
  }

  cancel(): void {
    this.goBack();
  }

  // ---------------------------------------------------------------------------
  // Formatting
  // ---------------------------------------------------------------------------
  formatFieldName(key: string): string {
    return (key || '')
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------
  private buildPayload(): any {
    const headerMapping: { [k: string]: string | null } = {};
    const fieldMapping: { [k: string]: string } = {};

    /** Canonical keys used by invoice extraction / CSV (must match backend _HEADER_SCHEMA_TO_EXTRACT). */
    const headerExtractKeys: { [k: string]: string } = {
      invno: 'invoice_number',
      invdate: 'date',
      pono: 'order_no',
      store: 'store_code',
      suppname: 'supplier_name',
      cur: 'currency',
      taxable: 'total_taxable_value',
      vat: 'total_VAT',
      vendorinv: 'vendor_invoice_value',
      netdisc: 'net_value_after_discount',
      netaddchg: 'net_value_after_add_charges',
      nettax: 'net_value_after_tax',
      grand: 'grand_total',
      refno: 'reference_number',
      deliv: 'delivery_date'
    };
    /** Canonical line keys used by extraction (must match backend _LINE_LABEL_TO_EXTRACT). */
    const lineExtractKeys: { [label: string]: string } = {
      'Item Code': 'Code',
      'Description': 'Description',
      'Pack Size': 'Size',
      'Quantity': 'QTY',
      'UOM': 'UOM',
      'Unit Price': 'Price',
      'Discount %': 'ITEM DISC PER 1',
      'Discount % 1': 'ITEM DISC PER 1',
      'Discount Amount 1': 'DISCOUNT_AMOUNT',
      'Discount Rate': 'ITEM DISC PER 1',
      'Discount Rate 1': 'ITEM DISC PER 1',
      'Discount % 2': 'ITEM DISC PER 2',
      'Discount Amount 2': 'DISCOUNT_AMOUNT_2',
      'Discount Rate 2': 'ITEM DISC PER 2',
      'Discount % 3': 'ITEM DISC PER 3',
      'Discount Amount 3': 'DISCOUNT_AMOUNT_3',
      'Discount Rate 3': 'ITEM DISC PER 3',
      'VAT Rate': 'VAT RATE',
      'Total Exclusive': 'TOTAL EXCLUSIVE',
      'VAT Amount': 'VAT AMT',
      'Total Inclusive': 'TOTAL INCLUSIVE'
    };

    this.visibleHeaderFields.forEach(f => {
      const dk = this.headerMapping[f.key] || null;
      headerMapping[f.key] = dk;
      if (dk) {
        fieldMapping[dk] = headerExtractKeys[f.key] || f.label;
      }
    });

    const lineColumnMapping: { [k: string]: string | null } = {};
    this.detectedColumns.forEach(c => {
      if (this.hiddenLineColumns.includes(c.key)) {
        return;
      }
      const lbl = this.lineColumnMapping[c.key] || null;
      // Persist selected label; Amount → DISCOUNT_AMOUNT_N, % / Rate → ITEM DISC PER N.
      lineColumnMapping[c.key] = lbl;
      if (lbl) {
        const canon = this.canonicalizeLineLabel(lbl) || lbl;
        fieldMapping[c.key] = lineExtractKeys[lbl] || lineExtractKeys[canon] || lbl;
      }
    });

    const headerManualValues: { [k: string]: string } = {};
    Object.keys(this.headerManualValues || {}).forEach(key => {
      const val = (this.headerManualValues[key] || '').trim();
      if (val) {
        headerManualValues[key] = val;
      }
    });

    const customFields = {
      ...(this.customFields || {}),
      mappingRows: {
        headerExtras: this.extraHeaderFields.map(f => ({ key: f.key, label: f.label })),
        deletedHeaderKeys: this.deletedHeaderKeys,
        extraLineLabels: this.extraLineLabels,
        manualLineColumns: this.manualLineColumns.map(c => ({ key: c.key, sample: c.sample })),
        hiddenLineColumns: this.hiddenLineColumns,
        headerManualValues
      }
    };

    return {
      schemaVersion: 2,
      headerMapping,
      lineColumnMapping,
      headerManualValues,
      fieldMapping,
      fieldVisibility: this.fieldVisibility || {},
      customFields,
      deletedFields: this.deletedFields || []
    };
  }

  saveDraft(): void {
    this.basicService.showprogress = true;
    this.documentMappingService.saveFieldMapping(this.documentId, this.buildPayload()).toPromise().then((response: any) => {
      this.basicService.showprogress = false;
      if (response && response.status === 'success') {
        this.dialog.success('Draft Saved', 'Vendor template saved as draft.');
      } else {
        this.dialog.error('Error', (response && response.message) || 'Failed to save template.');
      }
    }).catch(() => {
      this.basicService.showprogress = false;
      this.dialog.error('Error', 'Failed to save template. Please try again.');
    });
  }

  publish(): void {
    if (!this.canPublish) {
      this.dialog.warning('Incomplete Template', `${this.totalMandatory - this.doneMandatory} mandatory mapping(s) still remaining.`);
      return;
    }
    this.dialog.confirm({
      title: `Publish template for ${this.supplierName}?`,
      message: 'All mandatory fields and line-item columns are mapped. Once published, this template applies to every future invoice uploaded by this supplier. You can edit and re-publish at any time; existing invoices are not reprocessed.',
      confirmText: 'Publish Template',
      cancelText: 'Cancel',
      type: 'info',
      confirmTone: 'success'
    }).then((ok) => {
      if (!ok) {
        return;
      }
      this.basicService.showprogress = true;
      this.documentMappingService.saveFieldMapping(this.documentId, this.buildPayload()).toPromise().then((saveRes: any) => {
        if (!saveRes || saveRes.status !== 'success') {
          this.basicService.showprogress = false;
          this.dialog.error('Error', (saveRes && saveRes.message) || 'Failed to save template before publishing.');
          return;
        }
        this.documentMappingService.publishDocumentMapping(this.documentId).toPromise().then((pubRes: any) => {
          this.basicService.showprogress = false;
          if (pubRes && pubRes.status === 'success') {
            this.documentStatus = 5;
            this.dialog.success('Template Published', 'This vendor template is now live and will apply to future invoices from this supplier.');
          } else {
            this.dialog.error('Error', (pubRes && pubRes.message) || 'Failed to publish template.');
          }
        }).catch(() => {
          this.basicService.showprogress = false;
          this.dialog.error('Error', 'Failed to publish template. Please try again.');
        });
      }).catch(() => {
        this.basicService.showprogress = false;
        this.dialog.error('Error', 'Failed to save template before publishing.');
      });
    });
  }

  deleteTemplate(): void {
    if (!this.documentId) {
      return;
    }
    const publishedNote = this.isPublished
      ? ' This supplier will no longer have a published template for future invoices.'
      : '';
    this.dialog.confirm({
      title: 'Delete Template?',
      message: `Delete the vendor template for ${this.supplierName}? This cannot be undone.${publishedNote}`,
      confirmText: 'Yes, delete it',
      cancelText: 'Cancel',
      danger: true
    }).then((ok) => {
      if (!ok) {
        return;
      }
      this.basicService.showprogress = true;
      this.documentMappingService.deleteDocument(this.documentId).toPromise().then((response: any) => {
        this.basicService.showprogress = false;
        if (response && response.status === 'success') {
          this.dialog.success('Deleted', 'Vendor template deleted.').then(() => this.cancel());
        } else {
          this.dialog.error('Error', (response && response.message) || 'Failed to delete template.');
        }
      }).catch(() => {
        this.basicService.showprogress = false;
        this.dialog.error('Error', 'Failed to delete template. Please try again.');
      });
    });
  }
}
